// components
import { MenuItem } from '@mui/material';
import GridPageAbstract from '../abstract/GridPageAbstract';
import Iconify from '../../components/iconify';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import { TG_WEBHOOK_STATUS } from '../../constants/tgWebhookStatus';
import TgUnknownChatsDialog from '../dialogs/TgUnknownChatsDialog';
import i18n from '../../i18n';

export default class TgBotsPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicTgBots';
        this.Server = this.props.context.getState().server.publicTgBots;
        this.MenuDisplayType = 'menu';
        this.searchFields = ['name'];

        const defaultHead = [
            { id: 'name', required: true, label: 'name', alignRight: false, cell: 'Text', displayGrid: true },

            // Credentials. The API never returns either of these, so the editor
            // opens them blank on an existing bot — which is the intended
            // affordance: leave blank to keep what is stored, type to rotate.
            // displayGrid false because a grid column that is always empty only
            // invites someone to "fix" it by pasting the token in. No sort either:
            // a sort on a column the API never returns is an oracle on it, and the
            // backend refuses it (gap review #3).
            {
                id: 'botToken',
                label: 'botToken',
                alignRight: false,
                cell: 'Text',
                displayGrid: false,
                disableSort: true,
                helperText: 'tgBotTokenHelper'
            },
            {
                id: 'webhookSecret',
                label: 'webhookSecret',
                alignRight: false,
                // Carries a generate button. The value goes to Telegram verbatim
                // and setWebhook only accepts A-Za-z0-9_- , so a hand-typed or
                // password-manager secret with punctuation in it is refused — a
                // minute later, as webhook_status = failed, far from this field.
                cell: 'Secret',
                displayGrid: false,
                disableSort: true,
                helperText: 'tgWebhookSecretHelper'
            },

            {
                id: 'webhookBaseUrl',
                label: 'webhookBaseUrl',
                alignRight: false,
                cell: 'Url',
                displayGrid: true,
                helperText: 'tgWebhookBaseUrlHelper'
            },
            {
                id: 'apiBase',
                label: 'apiBase',
                alignRight: false,
                cell: 'Text',
                displayGrid: false,
                helperText: 'tgApiBaseHelper'
            },
            {
                id: 'autoRegisterWebhook',
                label: 'autoRegisterWebhook',
                alignRight: false,
                cell: 'Boolean',
                displayGrid: true,
                helperText: 'tgAutoRegisterWebhookHelper'
            },

            // Registration status, written by core-cron. Read-only in the same way
            // the api-balance fields are: a request cannot assert what Telegram was
            // actually told. Before these existed a failed registration left nothing
            // behind but a Confirm button that quietly stopped working.
            {
                id: 'webhookStatus',
                label: 'webhookStatus',
                alignRight: false,
                cell: 'Items',
                items: TG_WEBHOOK_STATUS,
                displayGrid: true,
                disableEditor: true
            },
            { id: 'webhookError', label: 'webhookError', alignRight: false, cell: 'BigText', displayGrid: true, disableEditor: true },
            { id: 'webhookRegisteredAt', label: 'webhookRegisteredAt', alignRight: false, cell: 'DateTime', displayGrid: true, disableEditor: true },
            { id: 'webhookLastAttemptAt', label: 'webhookLastAttemptAt', alignRight: false, cell: 'DateTime', displayGrid: false, disableEditor: true },

            { id: 'isActive', label: 'isActive', alignRight: false, cell: 'Enable', displayGrid: true },
            { id: 'isArchived', label: 'isArchived', alignRight: false, cell: 'Boolean', displayGrid: true },
            {
                id: 'modifiedReason',
                label: 'modifiedReason',
                alignRight: false,
                cell: 'BigText',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head,
            unknownChatsBot: null
        };
    }

    openUnknownChats = () => {
        const id = this.state.menuClickItemId;
        const bot = this.List.find((item) => item.id === id);
        this.handleCloseMenu();
        this.setState({ unknownChatsBot: bot });
    };

    closeUnknownChats = () => {
        this.setState({ unknownChatsBot: null });
    };

    renderCustomMenuItems(id) {
        // An array, not a fragment: hasMenuItems counts custom items with
        // Array.isArray(...) && length > 0, so a bare element does not register.
        //
        // Not gated on a separate permission. public-tg-unknown-chats is readable
        // by exactly the roles that can read public-tg-bots — admin only — so a
        // user who can see this grid can always see the chats behind it, and a
        // second check would only be a way to get it wrong.
        return [
            <MenuItem key={`${id}-unknown-chats`} onClick={this.openUnknownChats}>
                <Iconify icon="eva:message-circle-outline" sx={{ mr: 2 }} />
                {i18n.t('dialogs.tgUnknownChats')}
            </MenuItem>
        ];
    }

    renderCustom() {
        const { unknownChatsBot } = this.state;
        if (!unknownChatsBot) return null;

        return (
            <TgUnknownChatsDialog
                openDialog
                bot={unknownChatsBot}
                closeDialog={this.closeUnknownChats}
                server={this.props.context.getState().server.publicTgUnknownChats}
            />
        );
    }
}
